import { useEffect, useMemo, useState } from 'react'
import { listEvents, listSpeakers, listTags } from '../lib/api.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import Filters from '../components/Filters.jsx'
import EventMap, { hasCoords } from '../components/EventMap.jsx'
import MapLegend from '../components/MapLegend.jsx'
import { PAGE_STANDARD } from '../lib/layout.js'
import { isPastEvent, includesPast } from '../lib/dateRange.js'
import useFilterRoute from '../hooks/useFilterRoute.js'
import NotFound from '../components/NotFound.jsx'

const MAP_PAGE_SIZE = 1000

function paramsForApi(filters) {
  return {
    speaker_id: filters.speakerId || undefined,
    tag: filters.tag || undefined,
    from: filters.from ? new Date(`${filters.from}T00:00:00Z`).toISOString() : undefined,
    to: filters.to ? new Date(`${filters.to}T23:59:59Z`).toISOString() : undefined,
    page: 0,
    size: MAP_PAGE_SIZE,
  }
}

export default function MapPage() {
  // #338: /map/2026 works exactly like /2026. Same hook, different base path,
  // so the map's own range links stay under /map instead of bouncing the
  // reader onto the calendar.
  const { year, invalidYear, filters, setFilters } = useFilterRoute('/map')
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [speakers, setSpeakers] = useState([])
  const [tags, setTags] = useState([])

  useEffect(() => {
    listSpeakers().then(setSpeakers).catch(() => {})
    listTags().then(setTags).catch(() => {})
  }, [])

  const filterKey = JSON.stringify(filters)
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listEvents(paramsForApi(filters))
      .then((data) => {
        if (cancelled) return
        setItems(data.content || [])
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey])

  const plottable = useMemo(() => items.filter(hasCoords), [items])
  const missingCoords = items.length - plottable.length
  // #334: the legend only earns its space once there is something to explain —
  // with the default "Upcoming" range every pin is the same colour.
  const pastPlotted = useMemo(
    () => plottable.filter((e) => isPastEvent({ startsAtUtc: e.endsAtUtc || e.startsAtUtc })).length,
    [plottable],
  )

  if (invalidYear) return <NotFound />

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          <span className="brand-gradient-text">Map</span>
          {year ? <span className="text-slate-900">{` ${year}`}</span> : null}
        </h1>
        <p className="mt-2 text-base text-slate-600">
          {includesPast(filters)
            ? 'Talks plotted by venue — past ones in amber. Pinch to zoom, tap a marker for details.'
            : 'Upcoming talks plotted by venue. Pinch to zoom, tap a marker for details.'}
        </p>
      </header>

      <div className="space-y-4">
        {/* #355: `compact` drops Country / Region / City, and that is
            deliberate rather than an oversight — on a map, panning and zooming
            IS the place filter, and a text box that says "US" while the
            viewport shows Australia is a contradiction the user has to resolve.
            Speaker, tag and date have no spatial equivalent, so they stay. */}
        <Filters value={filters} onChange={setFilters} speakers={speakers} tags={tags}
                 compact basePath="/map" />

        {error ? (
          <ErrorAlert
            title="Couldn't load the map"
            message={error.message || String(error)}
            source="MapPage"
          />
        ) : null}

        {loading && items.length === 0 ? (
          <div className="rounded-2xl bg-white p-10 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
            Loading map…
          </div>
        ) : plottable.length === 0 && !error ? (
          <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
            <h3 className="text-base font-semibold text-slate-900">No events on the map</h3>
            <p className="mt-1 text-sm text-slate-500">
              {items.length > 0
                ? `${items.length} matching ${items.length === 1 ? 'event has' : 'events have'} no coordinates yet.`
                : 'Try clearing a filter or widening the date range.'}
            </p>
          </div>
        ) : (
          <>
            {/* Above the map, not below it: the map is 70vh tall, so a key
                underneath would sit off-screen exactly when it is needed. */}
            {pastPlotted > 0 ? <MapLegend pastCount={pastPlotted} /> : null}
            <EventMap events={plottable} />
            {missingCoords > 0 ? (
              <p className="text-center text-xs text-slate-500">
                {missingCoords} {missingCoords === 1 ? 'event is' : 'events are'} not shown — no coordinates yet.
              </p>
            ) : null}
          </>
        )}
      </div>
    </main>
  )
}
